import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  Column,
  Columns,
  Dialog,
  Hidden,
  SelectField,
  Stack,
  Text,
  TextField,
} from "@origin-digital/ods-core";
import { IFormProps } from "../FormAccordionContainer";
import {
  australianStates,
  streetTypeOptions,
  poBoxTypes,
  premiseTypes,
  streetSuffixes,
} from "./AddressConstants";
import {
  Field,
  FieldMetaProps,
  Formik,
  FormikProps,
  useFormikContext,
} from "formik";
import { billingAddressValidationSchema } from "./Validations";
import { AccountBlockValues, BillingAddressValues, FormValues } from "../types";
import {
  billingAddressEmptyFields,
  formikFieldsBatchUpdater,
  generateBillingAddressString,
  poboxEmptyFields,
} from "../helper";

interface IAddressDialogProps extends Partial<IFormProps> {
  open: boolean;
  setDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

const BillingAddressDialog = (props: IAddressDialogProps) => {
  const { open, setDialog } = props;

  const { values, setFieldValue } = useFormikContext<AccountBlockValues>();
  const emptyLocalData = {
    state: "",
    suburb: "",
    postcode: "",
    unitNumber: "",
    premiseType: "",
    streetNumber: "",
    streetName: "",
    level: "",
    streetSuffix: "",
    streetType: "",
    pobox: false,
    poboxNumber: "",
    poboxType: "",
  };

  const handleApply = (newAddressData: BillingAddressValues) => {
    setFieldValue("billing-address", newAddressData);
  };
  return (
    <Dialog
      id="billing-address-dialog"
      open={open}
      title="Billing address"
      maxWidth="xl"
      onClose={() => setDialog(false)}
    >
      {/* Outer stack */}
      <Formik<BillingAddressValues>
        initialValues={values["billing-address"]}
        validationSchema={billingAddressValidationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={(v) => {
          handleApply(v);
          setFieldValue(
            "billingAddressString",
            generateBillingAddressString(v)
          );
          setDialog(false);
        }}
      >
        {({
          errors,
          submitForm,
          setFieldValue: setInnerFieldValue,
          values: innerValues,
        }) => {
          return (
            <Stack space="small">
              {/* Box hack to fix issue with TextField label being cropped by Dialog title */}
              <Box />
              {/* End hack */}

              {/* Inner stack */}
              <Stack
                space={{
                  xs: "medium",
                  lg: "xlarge",
                }}
              >
                {/* Suburb, Postcode & State/Territory */}
                <Columns
                  collapseBelow="lg"
                  space={{
                    xs: "small",
                    lg: "xlarge",
                  }}
                >
                  <Column width="3/4">
                    <Columns
                      collapseBelow="lg"
                      space={{
                        xs: "xlarge",
                        lg: "medium",
                      }}
                    >
                      <Column width="1/2">
                        <Field name="suburb" id="suburb">
                          {({
                            field,
                          }: {
                            field: FormikProps<BillingAddressValues>;
                          }) => (
                            <TextField
                              {...field}
                              id="suburb"
                              variant="outlined"
                              label="Suburb"
                              hideHelperTextSpace
                              error={!!errors.suburb}
                              helperText={errors.suburb}
                            />
                          )}
                        </Field>
                      </Column>
                      <Column>
                        <Columns space="medium">
                          <Column>
                            <Field name="postcode" id="postcode">
                              {({
                                field,
                                meta,
                              }: {
                                field: FormikProps<BillingAddressValues>;
                                meta: FieldMetaProps<FormValues>;
                              }) => (
                                <TextField
                                  {...field}
                                  id="postcode"
                                  variant="outlined"
                                  label="Postcode"
                                  maxLength="4"
                                  hideHelperTextSpace
                                  error={!!errors.postcode}
                                  helperText={meta.error}
                                />
                              )}
                            </Field>
                          </Column>
                          <Column>
                            <Field name="state" id="state">
                              {({
                                field,
                              }: {
                                field: FormikProps<BillingAddressValues>;
                              }) => (
                                <SelectField
                                  {...field}
                                  id="state"
                                  variant="outlined"
                                  label="State"
                                  displayEmpty={true}
                                  emptyAriaLabel="Empty"
                                  //defaultValue="qld"
                                  options={australianStates}
                                  hideHelperTextSpace
                                  error={!!errors.state}
                                  onChange={(e) => {
                                    setInnerFieldValue!(
                                      "state",
                                      e.target.value
                                    );
                                  }}
                                  //helperText="helperText"
                                />
                              )}
                            </Field>
                          </Column>
                        </Columns>
                      </Column>
                    </Columns>
                  </Column>
                  <Column width="1/3" />
                </Columns>
                {/* Suburb, Postcode & State/Territory */}

                {/* PO Box */}
                <Columns
                  collapseBelow="lg"
                  space={{
                    xs: "xsmall",
                    lg: "xlarge",
                  }}
                  alignY="stretch"
                >
                  <Column width="3/4">
                    <Checkbox
                      id="pobox"
                      label="PO Box"
                      checked={innerValues.pobox}
                      error={!!errors.pobox}
                      onChange={(e) => {
                        setInnerFieldValue("pobox", e.target.checked);
                        if (e.target.checked) {
                          Object.keys(billingAddressEmptyFields).forEach(
                            (k) => {
                              setInnerFieldValue(
                                `${k}`,
                                billingAddressEmptyFields[
                                  k as keyof typeof billingAddressEmptyFields
                                ]
                              );
                            }
                          );
                        } else {
                          Object.keys(poboxEmptyFields).forEach((k) => {
                            setInnerFieldValue(
                              `${k}`,
                              poboxEmptyFields[
                                k as keyof typeof poboxEmptyFields
                              ]
                            );
                          });
                        }
                      }}
                    />
                  </Column>
                  <Column width="1/4" />
                </Columns>
                {/* End PO Box */}

                {/* Unit/Premise No, Type of Premise, Level & Building name */}
                {innerValues.pobox ? (
                  <Columns
                    collapseBelow="lg"
                    space={{
                      xs: "small",
                      lg: "xlarge",
                    }}
                    alignY="stretch"
                  >
                    <Column width="3/4">
                      <Columns
                        collapseBelow="lg"
                        space={{
                          xs: "xlarge",
                          lg: "medium",
                        }}
                      >
                        <Column>
                          <Columns space="medium">
                            <Column>
                              <SelectField
                                id="pobox-type"
                                variant="outlined"
                                label="Box type"
                                displayEmpty={true}
                                emptyAriaLabel="Empty"
                                value={innerValues.poboxType}
                                error={!!errors.poboxType}
                                options={poBoxTypes}
                                onChange={(e) => {
                                  setInnerFieldValue!(
                                    "poboxType",
                                    e.target.value
                                  );

                                  // Find the selected option and clear poboxNumber if optionalPoNumber is true
                                  const selectedOption = poBoxTypes.find(
                                    (option) => option.value === e.target.value
                                  );
                                  if (
                                    selectedOption?.optionalPoNumber === true
                                  ) {
                                    setInnerFieldValue!("poboxNumber", "");
                                  }
                                }}
                                hideHelperTextSpace
                                //helperText="helperText"
                              />
                            </Column>
                            {innerValues.poboxType === "carePo" ||
                            innerValues.poboxType === "CMA" ||
                            innerValues.poboxType === "CPA" ? null : (
                              <Column>
                                <Field name="poboxNumber" id="pobox-type">
                                  {({
                                    field,
                                    meta,
                                  }: {
                                    field: FormikProps<BillingAddressValues>;
                                    meta: FieldMetaProps<BillingAddressValues>;
                                  }) => (
                                    <TextField
                                      {...field}
                                      id="pobox-number"
                                      variant="outlined"
                                      label="PO Box number"
                                      hideHelperTextSpace
                                      error={!!meta.error}
                                      helperText={meta.error}
                                    />
                                  )}
                                </Field>
                              </Column>
                            )}
                          </Columns>
                        </Column>
                        <Column />
                      </Columns>
                    </Column>
                    <Column width="1/4" />
                  </Columns>
                ) : (
                  <Stack>
                    <Columns
                      collapseBelow="lg"
                      space={{
                        xs: "small",
                        lg: "xlarge",
                      }}
                      alignY="stretch"
                    >
                      <Column width="3/4">
                        <Columns
                          collapseBelow="lg"
                          space={{
                            xs: "xlarge",
                            lg: "medium",
                          }}
                        >
                          <Column>
                            <Columns space="medium">
                              <Column>
                                <Field name="unitNumber" id="unit-number">
                                  {({
                                    field,
                                    meta,
                                  }: {
                                    field: FormikProps<BillingAddressValues>;
                                    meta: FieldMetaProps<BillingAddressValues>;
                                  }) => (
                                    <TextField
                                      {...field}
                                      id="unitNumber"
                                      variant="outlined"
                                      label="Unit/Premises No."
                                      error={!!meta.error}
                                      hideHelperTextSpace
                                      helperText={meta.error}
                                    />
                                  )}
                                </Field>
                              </Column>
                              <Column>
                                <SelectField
                                  id="premiseType"
                                  variant="outlined"
                                  label="Premises type"
                                  displayEmpty={true}
                                  value={innerValues.premiseType}
                                  emptyAriaLabel="Empty"
                                  options={premiseTypes}
                                  hideHelperTextSpace
                                  onChange={(e) => {
                                    setInnerFieldValue!(
                                      "premiseType",
                                      e.target.value
                                    );
                                  }}
                                  error={!!errors.premiseType}
                                  helperText={errors.premiseType}
                                  //helperText="helperText"
                                />
                              </Column>
                            </Columns>
                          </Column>
                          <Column>
                            <Columns space="medium">
                              <Column>
                                <Field name="level" id="level">
                                  {({
                                    field,
                                  }: {
                                    field: FormikProps<BillingAddressValues>;
                                  }) => (
                                    <TextField
                                      {...field}
                                      id="level"
                                      variant="outlined"
                                      label="Level"
                                      hideHelperTextSpace
                                      error={!!errors.level}
                                      helperText={errors.level}
                                      //helperText="helperText"
                                    />
                                  )}
                                </Field>
                              </Column>
                            </Columns>
                          </Column>
                        </Columns>
                      </Column>
                      <Column width="1/4">
                        <Box display="flex" alignItems="stretch" height="full">
                          <Hidden below="lg">
                            {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
                            <Box
                              style={{ borderLeft: "1px solid #E3E3E3" }}
                              paddingRight="medium"
                              height="full"
                            />
                            {/* End Faux vertical <Divider> */}
                          </Hidden>
                          <Box
                            alignSelf="center"
                            paddingBottom={{
                              xs: "medium",
                              lg: "none",
                            }}
                          >
                            <Text variant="body-small" tone="neutralLight">
                              Please specify the premise’s type if there’s a
                              unit/premise number.
                            </Text>
                          </Box>
                        </Box>
                      </Column>
                    </Columns>
                    {/* End Unit/Premise No, Type of Premise, Level & Building name */}

                    {/* Street No., Block, Section, DP */}
                    <Columns
                      collapseBelow="lg"
                      space={{
                        xs: "xxsmall",
                        sm: "small",
                        lg: "xlarge",
                      }}
                      alignY="stretch"
                    >
                      <Column width="3/4">
                        <Columns collapseBelow="lg" space="medium">
                          <Column>
                            <Columns space="medium" collapseBelow="sm">
                              <Column>
                                <Field name="streetNumber" id="street-number">
                                  {({
                                    field,
                                  }: {
                                    field: FormikProps<BillingAddressValues>;
                                  }) => (
                                    <TextField
                                      {...field}
                                      id="streetNumber"
                                      variant="outlined"
                                      label="Street No."
                                      hideHelperTextSpace
                                      helperText={
                                        errors.streetNumber
                                          ? errors.streetNumber
                                          : "e.g. 12 or 12A"
                                      }
                                      error={!!errors.streetNumber}
                                    />
                                  )}
                                </Field>
                              </Column>
                              <Column />
                            </Columns>
                          </Column>
                        </Columns>
                      </Column>
                      <Column width="1/4" />
                    </Columns>
                    {/* End Street No., Block, Section, DP */}

                    {/* Street name, Street type, Suffix */}
                    <Columns
                      collapseBelow="lg"
                      space={{
                        xs: "small",
                        lg: "xlarge",
                      }}
                    >
                      <Column width="3/4">
                        <Columns
                          collapseBelow="lg"
                          space={{
                            xs: "xlarge",
                            lg: "medium",
                          }}
                        >
                          <Column>
                            <Field name="streetName" id="street-name">
                              {({
                                field,
                              }: {
                                field: FormikProps<BillingAddressValues>;
                              }) => (
                                <TextField
                                  {...field}
                                  id="streetName"
                                  variant="outlined"
                                  label="Street name"
                                  hideHelperTextSpace
                                  error={!!errors.streetName}
                                  helperText={errors.streetName}
                                />
                              )}
                            </Field>
                          </Column>
                          <Column>
                            <Columns space="medium">
                              <Column>
                                <SelectField
                                  id="streetType"
                                  value={innerValues.streetType}
                                  variant="outlined"
                                  label="Street type"
                                  displayEmpty={true}
                                  emptyAriaLabel="Empty"
                                  options={streetTypeOptions}
                                  hideHelperTextSpace
                                  error={!!errors.streetType}
                                  //helperText="helperText"
                                  onChange={(e) => {
                                    setInnerFieldValue!(
                                      "streetType",
                                      e.target.value
                                    );
                                  }}
                                />
                              </Column>
                              <Column>
                                <SelectField
                                  id="streetSuffix"
                                  value={innerValues.streetSuffix}
                                  variant="outlined"
                                  label="Suffix"
                                  displayEmpty={true}
                                  emptyAriaLabel="Empty"
                                  options={streetSuffixes}
                                  hideHelperTextSpace
                                  error={!!errors.streetSuffix}
                                  onChange={(e) => {
                                    setInnerFieldValue!(
                                      "streetSuffix",
                                      e.target.value
                                    );
                                  }}
                                  //helperText="helperText"
                                />
                              </Column>
                            </Columns>
                          </Column>
                        </Columns>
                      </Column>
                      <Column width="1/4" />
                    </Columns>
                  </Stack>
                )}
                {/* End Street name, Street type, Suffix */}

                {/* Actions */}
                <Box
                  position={{
                    lg: "static",
                    xs: "fixed",
                  }}
                  paddingTop={{
                    lg: "medium",
                    xs: "medium",
                  }}
                  paddingBottom={{
                    lg: "none",
                    xs: "medium",
                  }}
                  paddingX={{
                    lg: "none",
                    xs: "medium",
                  }}
                  backgroundColor="white"
                  width="full"
                  style={{
                    left: "0",
                    bottom: "0",
                    zIndex: "1",
                    //borderTop: "1px solid #E3E3E3",
                  }}
                >
                  <Box
                    display="flex"
                    flexWrap={{
                      lg: "nowrap",
                      xs: "wrap",
                    }}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Button
                      variant="text"
                      color="primary"
                      noTextPadding
                      onClick={() =>
                        formikFieldsBatchUpdater(
                          setInnerFieldValue,
                          emptyLocalData
                        )
                      }
                    >
                      Clear
                    </Button>
                    <Box display="flex">
                      <Box marginRight="large">
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() => setDialog(false)}
                        >
                          Cancel
                        </Button>
                      </Box>
                      <Button
                        onClick={() => {
                          submitForm();
                        }}
                      >
                        Apply
                      </Button>
                    </Box>
                  </Box>
                </Box>
                {/* End Actions */}
              </Stack>
              {/* End Inner stack */}
            </Stack>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default BillingAddressDialog;
